import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { CampaignModel, CampaignValidationModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/campaignValidation/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/campaignValidation/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/campaignValidation/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/campaignValidation/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/campaignValidation/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/campaignValidation/CREATE_FAILURE'

export type CampaignValidationModuleState = {
  creating: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
}

// Initial State
const initialState: CampaignValidationModuleState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCampaignValidations(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaignValidations.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/campaign-validations', options)
      .then(({ data }: { data: { data: CampaignValidationModel[] } }) => {
        const normalizedJson = normalize(data.data, Schemas.CAMPAIGN_VALIDATION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data: data.data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function validateCampaign(campaign: CampaignModel) {
  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    dispatch(createRequest())

    const promise = api(`/campaign-validations/${campaign.id}/validate`, {}, config)
      .then(({ data }: { data: CampaignValidationModel[] }) => {

        // Remove old validations for the campaign
        const { campaignValidations } = getState().entities        
        Object.values(campaignValidations)
          .filter(validation => validation.campaignId === campaign.id)
          .forEach(validation => removeCampaignValidation(dispatch, validation))

        // Add new validations to the Redux store
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_VALIDATION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

function removeCampaignValidation(dispatch: AppDispatch, campaignValidation: CampaignValidationModel) {
  const normalizedJson = normalize(campaignValidation, Schemas.CAMPAIGN_VALIDATION)
  dispatch(deleteEntity(normalizedJson))
}

// Reducer
export default function reducer(
  state: CampaignValidationModuleState = initialState,
  action: Action = { type: '' },
): CampaignValidationModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
